/**
 * Extracts annotation mark character ranges from a ProseMirror document by
 * correlating the PM node tree with the serialized markdown string.
 *
 * Strategy
 * --------
 * We walk the PM document in the same order that the markdown serializer does
 * (depth-first, left-to-right), accumulating a character offset counter as we
 * encounter text content. When we enter a text node that carries an annotation
 * mark, we record { id, from, to } where from/to are the character offsets of
 * that text node in the markdown string.
 *
 * For annotation marks that span multiple consecutive text nodes (e.g. when a
 * mark boundary splits a run), we merge adjacent runs with the same annotation
 * ID into a single contiguous range.
 *
 * Limitations
 * -----------
 * The character offset tracking works because the markdown serializer visits
 * nodes in the same depth-first order and emits text content contiguously.
 * However, the serializer also emits structural characters that don't appear
 * in the PM document (e.g. `**` for bold, `##` for headings, `\n\n` between
 * blocks). We account for these by searching for each text run in the markdown
 * string starting from our current offset — this makes the extraction robust
 * to any decorative characters the serializer inserts around text content.
 *
 * The search is always forward-only (we never scan backwards), so it correctly
 * handles repeated text content as long as the PM node order matches the
 * markdown output order — which it does for all standard handlers.
 */

import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { MarkdownSourceAnnotationRange } from './types';

/**
 * Escape a raw PM text string the same way `mdast-util-to-markdown` does, so
 * we can search for it in the serialized markdown when a direct `indexOf` fails.
 *
 * `mdast-util-to-markdown` escapes the following characters with a leading
 * backslash when they appear in plain text content: `*`, `_`, `` ` ``, `[`, `\`.
 * It does this character-by-character, so "hello *world" → "hello \*world".
 *
 * We replicate that transform here so that when `indexOf(rawText)` returns -1
 * (because the serializer inserted backslashes into the text), we can retry
 * with `indexOf(escapedText)` and still find the correct position.
 *
 * Note: this escape is intentionally conservative — we only escape characters
 * that mdast-util-to-markdown is known to escape in plain text nodes. Other
 * transformations (e.g. entity encoding, which we undo in `undoEdgeWhitespaceEncoding`)
 * are handled separately.
 */
function escapedForMarkdown(text: string): string {
	return text.replace(/[*_`[\\\]]/gu, (ch) => `\\${ch}`);
}

/**
 * Walk the PM document and extract annotation mark character ranges relative
 * to the given markdown string.
 *
 * @param doc - The ProseMirror document node (already hydrated).
 * @param markdown - The markdown string produced by serializing `doc`.
 * @returns An array of annotation ranges, one per unique annotation ID span.
 *   Adjacent text runs with the same annotation ID are merged into a single range.
 */
export function extractAnnotationRanges(
	doc: PMNode,
	markdown: string,
): MarkdownSourceAnnotationRange[] {
	// Map from annotation ID → { from, to } of the current open range.
	// We merge consecutive text nodes that carry the same annotation ID.
	const openRanges = new Map<string, { from: number; to: number }>();
	const completed: MarkdownSourceAnnotationRange[] = [];

	// Current search position in the markdown string.
	// We advance this forward as we encounter text nodes.
	let searchFrom = 0;

	doc.descendants((node) => {
		if (!node.isText || !node.text) {
			return true; // descend into non-text nodes
		}

		const text = node.text;

		// Find the position of this text run in the markdown string.
		// We search forward from `searchFrom` to handle repeated text correctly.
		let pos = markdown.indexOf(text, searchFrom);
		// The serializer may have escaped special characters (e.g. `*` → `\*`),
		// making the raw PM text no longer a direct substring. Retry with the
		// escaped form as a fallback.
		let matchLength = text.length;
		if (pos === -1) {
			const escaped = escapedForMarkdown(text);
			if (escaped !== text) {
				pos = markdown.indexOf(escaped, searchFrom);
				if (pos !== -1) {
					// The match is against the escaped string in the markdown, so
					// the range covers `escaped.length` characters, not `text.length`.
					matchLength = escaped.length;
				}
			}
		}
		if (pos === -1) {
			// Still not found — genuinely untranslateable node (e.g. hard_break).
			// Skip offset tracking for this node without advancing searchFrom.
			return true;
		}

		const nodeFrom = pos;
		const nodeTo = pos + matchLength;

		// Collect the annotation IDs active on this text node.
		const activeIds = new Set<string>();
		for (const mark of node.marks) {
			if (mark.type.name === 'annotation' && mark.attrs.id) {
				activeIds.add(mark.attrs.id as string);
			}
		}

		// Close any open ranges whose ID is no longer active on this node.
		for (const [id, range] of openRanges) {
			if (!activeIds.has(id)) {
				completed.push({ id, from: range.from, to: range.to });
				openRanges.delete(id);
			}
		}

		// Extend or open ranges for active annotation IDs.
		for (const id of activeIds) {
			const existing = openRanges.get(id);
			if (existing) {
				// Extend the existing open range to cover this text node.
				existing.to = nodeTo;
			} else {
				// New annotation starting at this text node.
				openRanges.set(id, { from: nodeFrom, to: nodeTo });
			}
		}

		// Advance the search position past this text node.
		searchFrom = nodeTo;

		return true;
	});

	// Close any ranges still open at the end of the document.
	for (const [id, range] of openRanges) {
		completed.push({ id, from: range.from, to: range.to });
	}

	// Sort by start position for deterministic output.
	completed.sort((a, b) => a.from - b.from || a.id.localeCompare(b.id));

	return completed;
}
